import { formatInTimeZone } from 'date-fns-tz'

import { isSite12HourFormat } from '@hapana/shared/web/utils/date-format-utils'

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS
const STARTS_SOON_MS = 12 * HOUR_MS
const RELATIVE_DAY_LIMIT = 6
const ZONED_DAY_PATTERN = 'yyyy-MM-dd'

export const CoverTimeTone = {
  Urgent: 'urgent',
  Soon: 'soon',
  Later: 'later',
} as const
export type CoverTimeTone = (typeof CoverTimeTone)[keyof typeof CoverTimeTone]

export type CoverTimeLabel = {
  key: string
  values: Record<string, number>
  tone: CoverTimeTone
}

export type CoverDateTile = { weekday: string; day: string; month: string }

const toDate = (value: string | number | Date): Date | null => {
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const toZonedDayIndex = (date: Date, timezone: string): number => {
  const [year = 0, month = 1, day = 1] = formatInTimeZone(date, timezone, ZONED_DAY_PATTERN)
    .split('-')
    .map(Number)
  return Math.round(Date.UTC(year, month - 1, day) / DAY_MS)
}

const format = (
  date: Date,
  timezone: string,
  locale: string,
  options: Intl.DateTimeFormatOptions
): string => new Intl.DateTimeFormat(locale, { timeZone: timezone, ...options }).format(date)

const timeOptions = (): Intl.DateTimeFormatOptions => ({
  hour: 'numeric',
  minute: '2-digit',
  hour12: isSite12HourFormat(),
})

export const formatCoverTime = (
  value: string | number | Date,
  timezone: string,
  locale: string
): string => {
  const date = toDate(value)
  return date ? format(date, timezone, locale, timeOptions()) : ''
}

export const formatCoverTimeRange = (
  start: string | number | Date,
  end: string | number | Date | null,
  timezone: string,
  locale: string,
  { withZone = false }: { withZone?: boolean } = {}
): string => {
  const startDate = toDate(start)
  if (!startDate) {
    return ''
  }
  const endDate = end ? toDate(end) : null
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    ...timeOptions(),
    ...(withZone && { timeZoneName: 'short' }),
  }
  const formatter = new Intl.DateTimeFormat(locale, options)
  if (!endDate) {
    return formatter.format(startDate)
  }
  if (toZonedDayIndex(startDate, timezone) === toZonedDayIndex(endDate, timezone)) {
    return formatter.formatRange(startDate, endDate)
  }
  const startOnly = new Intl.DateTimeFormat(locale, { timeZone: timezone, ...timeOptions() })
  return `${startOnly.format(startDate)} – ${formatter.format(endDate)}`
}

export const formatCoverDay = (
  value: string | number | Date,
  timezone: string,
  locale: string
): string => {
  const date = toDate(value)
  return date
    ? format(date, timezone, locale, { weekday: 'short', day: 'numeric', month: 'short' })
    : ''
}

export const formatCoverDayTime = (
  value: string | number | Date,
  timezone: string,
  locale: string
): string => {
  const date = toDate(value)
  return date
    ? format(date, timezone, locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        ...timeOptions(),
      })
    : ''
}

export const formatCoverMoment = (
  value: string | number | Date,
  timezone: string,
  locale: string,
  now: number
): string => {
  const date = toDate(value)
  if (!date) {
    return ''
  }
  return toZonedDayIndex(date, timezone) === toZonedDayIndex(new Date(now), timezone)
    ? formatCoverTime(date, timezone, locale)
    : formatCoverDayTime(date, timezone, locale)
}

export const getCoverDateTile = (
  value: string | number | Date,
  timezone: string,
  locale: string
): CoverDateTile => {
  const date = toDate(value)
  if (!date) {
    return { weekday: '', day: '', month: '' }
  }
  return {
    weekday: format(date, timezone, locale, { weekday: 'short' }),
    day: format(date, timezone, locale, { day: 'numeric' }),
    month: format(date, timezone, locale, { month: 'short' }),
  }
}

export const getCoverRelativeStart = (
  start: string | number | Date,
  timezone: string,
  now: number
): CoverTimeLabel | null => {
  const date = toDate(start)
  if (!date) {
    return null
  }
  const untilStart = date.getTime() - now
  if (untilStart <= 0) {
    return null
  }
  if (untilStart < HOUR_MS) {
    return {
      key: 'core:covers.time.startsInMinutes',
      values: { count: Math.max(1, Math.ceil(untilStart / MINUTE_MS)) },
      tone: CoverTimeTone.Urgent,
    }
  }
  if (untilStart < STARTS_SOON_MS) {
    return {
      key: 'core:covers.time.startsInHours',
      values: { count: Math.floor(untilStart / HOUR_MS) },
      tone: CoverTimeTone.Urgent,
    }
  }
  const days = toZonedDayIndex(date, timezone) - toZonedDayIndex(new Date(now), timezone)
  if (days <= 0) {
    return { key: 'core:covers.time.today', values: {}, tone: CoverTimeTone.Soon }
  }
  if (days === 1) {
    return { key: 'core:covers.time.tomorrow', values: {}, tone: CoverTimeTone.Soon }
  }
  if (days <= RELATIVE_DAY_LIMIT) {
    return { key: 'core:covers.time.inDays', values: { count: days }, tone: CoverTimeTone.Later }
  }
  return null
}

export const isStartingWithinHours = (
  start: string | number | Date,
  hours: number,
  now: number
): boolean => {
  const date = toDate(start)
  if (!date) {
    return false
  }
  const untilStart = date.getTime() - now
  return untilStart >= 0 && untilStart <= hours * HOUR_MS
}

export const getCoverCountdown = (
  deadline: string | number | Date,
  now: number
): CoverTimeLabel | null => {
  const date = toDate(deadline)
  if (!date) {
    return null
  }
  const remaining = date.getTime() - now
  if (remaining <= 0) {
    return null
  }
  if (remaining < HOUR_MS) {
    return {
      key: 'core:covers.time.minutesLeft',
      values: { count: Math.max(1, Math.ceil(remaining / MINUTE_MS)) },
      tone: CoverTimeTone.Urgent,
    }
  }
  if (remaining < DAY_MS) {
    return {
      key: 'core:covers.time.hoursLeft',
      values: { count: Math.floor(remaining / HOUR_MS) },
      tone: CoverTimeTone.Soon,
    }
  }
  return {
    key: 'core:covers.time.daysLeft',
    values: { count: Math.floor(remaining / DAY_MS) },
    tone: CoverTimeTone.Later,
  }
}

export const getCoverAgo = (value: string | number | Date, now: number): CoverTimeLabel => {
  const date = toDate(value)
  const elapsed = date ? Math.max(0, now - date.getTime()) : 0
  if (elapsed < MINUTE_MS) {
    return { key: 'core:covers.time.justNow', values: {}, tone: CoverTimeTone.Later }
  }
  if (elapsed < HOUR_MS) {
    return {
      key: 'core:covers.time.minutesAgo',
      values: { count: Math.floor(elapsed / MINUTE_MS) },
      tone: CoverTimeTone.Later,
    }
  }
  if (elapsed < DAY_MS) {
    return {
      key: 'core:covers.time.hoursAgo',
      values: { count: Math.floor(elapsed / HOUR_MS) },
      tone: CoverTimeTone.Later,
    }
  }
  return {
    key: 'core:covers.time.daysAgo',
    values: { count: Math.floor(elapsed / DAY_MS) },
    tone: CoverTimeTone.Later,
  }
}

export const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : ''
  return `${first}${last}`.toUpperCase()
}

export const getFirstName = (name: string): string => name.trim().split(/\s+/)[0] ?? ''

export const getAvatarToneIndex = (seed: string, toneCount: number): number =>
  toneCount > 0
    ? [...seed].reduce((total, character) => total + character.charCodeAt(0), 0) % toneCount
    : 0

export const sortCoversByStart = <T extends { sessionStartTime: string }>(
  covers: readonly T[],
  direction: 'asc' | 'desc' = 'asc'
): T[] => {
  const sign = direction === 'asc' ? 1 : -1
  return [...covers].sort(
    (left, right) =>
      sign *
      (new Date(left.sessionStartTime).getTime() - new Date(right.sessionStartTime).getTime())
  )
}
